
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import { Check, Shield, Lock } from 'lucide-react';

const HowItWorks = () => {
  const processSteps = [
    {
      number: "1",
      title: "Crea tu cuenta",
      description: "Regístrate con tu correo o número. Es rápido y gratuito."
    },
    {
      number: "2", 
      title: "Explica tu situación",
      description: "Nuestro asistente te hace preguntas para entender tu caso."
    },
    {
      number: "3",
      title: "Recibe orientación",
      description: "Te mostramos opciones posibles para avanzar con tu proceso."
    },
    {
      number: "4",
      title: "Accede a recursos",
      description: "Descarga guías o formularios relevantes según tu situación."
    }
  ];

  const trustPillars = [
    {
      icon: Check,
      title: "Servicio Gratuito",
      description: "Nuestros servicios esenciales no tienen costo para que todos puedan acceder a orientación."
    },
    {
      icon: Shield,
      title: "Información Actualizada", 
      description: "Basada en fuentes oficiales y revisada regularmente."
    },
    {
      icon: Lock,
      title: "Privacidad Garantizada",
      description: "Tus datos están protegidos y nunca se comparten."
    }
  ];

  return (
    <div className="min-h-screen bg-background font-sans">
      <Navigation />
      
      {/* Add proper top margin to account for fixed navigation */}
      <div className="pt-20 px-4 py-12 md:py-16 max-w-6xl mx-auto">
        {/* Section 1: Process Overview */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-4 break-words">
              ¿Cómo Funciona Migrante Amigo?
            </h1>
          </div>

          <div className="space-y-8">
            {processSteps.map((step, index) => (
              <Card 
                key={index}
                className="shadow-lg border border-border bg-card hover:shadow-xl transition-all duration-300"
              >
                <CardContent className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-full flex items-center justify-center shadow-md border border-primary/20">
                        <span className="text-2xl md:text-3xl font-bold text-primary">
                          {step.number}
                        </span>
                      </div>
                    </div>
                    <div className="text-center md:text-left flex-1 min-w-0">
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-3 break-words">
                        {step.title}
                      </h2>
                      <p className="text-muted-foreground text-base md:text-lg leading-relaxed break-words">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA Block */}
        <div className="mb-20">
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 shadow-lg max-w-3xl mx-auto">
            <CardContent className="p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4 break-words">
                ¿Listo para comenzar?
              </h2>
              <p className="text-muted-foreground text-base md:text-lg mb-8 max-w-2xl mx-auto leading-relaxed break-words">
                Empieza ahora. Es rápido, gratis y confidencial.
              </p>
              <Button 
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-12 py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 min-h-[44px] w-full max-w-[320px]"
                onClick={() => window.location.href = '/login'}
              >
                Inicia tu proceso
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Section 2: Trust & Why Migrante Amigo Works */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4 break-words">
              ¿Por Qué Confiar en Migrante Amigo?
            </h2>
          </div>

          <div className="space-y-6">
            {trustPillars.map((pillar, index) => {
              const IconComponent = pillar.icon;
              return (
                <Card 
                  key={index}
                  className="shadow-lg border border-border bg-card"
                >
                  <CardContent className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center shadow-sm border border-primary/20">
                          <IconComponent className="h-8 w-8 text-primary" />
                        </div>
                      </div>
                      <div className="text-center md:text-left flex-1 min-w-0">
                        <h3 className="text-xl font-semibold text-foreground mb-3 break-words">
                          {pillar.title}
                        </h3>
                        <p className="text-muted-foreground text-base leading-relaxed break-words">
                          {pillar.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default HowItWorks;
